import { LitElement, html, nothing } from 'lit';
import { currentLang, t } from '../i18n';
import { api } from '../api';
import { showToast } from '../toast';
import { userTimezone } from '../state';
import { getErrorMessage } from '../types';
import { defineZenComponent } from './register';

// #3374: two-step sign-in (#3377) and the AI connections a person can end one by one (#3375).
// #3490: ending one's own use.
export interface AiConnection {
  id: string;
  client_id: string;
  client_name: string;
  connected_at: string;
  last_used_at: string;
  expires_at: string;
}

// #3776: the places an AI can bring summaries and judgments to; unset reads as the AI's own conversation.
const AI_CONVERSATION_PLACES = ['conversation', 'slack_dm'] as const;
type AiConversationPlace = (typeof AI_CONVERSATION_PLACES)[number];
const PLACE_LABEL_KEYS: Record<AiConversationPlace, string> = {
  conversation: 'aiConversationPlaceConversation',
  slack_dm: 'aiConversationPlaceSlackDm',
};

export function readAiConversationPlaces(raw: unknown): Record<string, AiConversationPlace> {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return {};
  const places: Record<string, AiConversationPlace> = {};
  for (const [clientId, value] of Object.entries(raw as Record<string, unknown>)) {
    if ((AI_CONVERSATION_PLACES as readonly unknown[]).includes(value)) places[clientId] = value as AiConversationPlace;
  }
  return places;
}

interface MfaSetup {
  factorId: string;
  qrCode: string;
  secret: string;
}

class ZenAccountSecuritySection extends LitElement {
  static properties = {
    mfaEnabled: { attribute: false },
    mfaLoading: { attribute: false },
    setup: { attribute: false },
    disabling: { attribute: false },
    code: { attribute: false },
    busy: { attribute: false },
    connections: { attribute: false },
    connectionsLoading: { attribute: false },
    aiOrder: { attribute: false },
    orderSaving: { attribute: false },
    aiConversation: { attribute: false },
    placeSavingId: { attribute: false },
    confirmingId: { attribute: false },
    revokingId: { attribute: false },
    ending: { attribute: false },
    endEmail: { attribute: false },
  };

  declare mfaEnabled: boolean;
  declare mfaLoading: boolean;
  declare setup: MfaSetup | null;
  declare disabling: boolean;
  declare code: string;
  declare busy: boolean;
  declare connections: AiConnection[];
  declare connectionsLoading: boolean;
  /** #3692: the principal's order of the connected AIs (client ids, first preferred). */
  declare aiOrder: string[];
  declare orderSaving: boolean;
  /** #3776: where each AI (client id) brings summaries and judgments when nobody is talking to it. */
  declare aiConversation: Record<string, AiConversationPlace>;
  declare placeSavingId: string;
  declare confirmingId: string;
  declare revokingId: string;
  declare ending: boolean;
  declare endEmail: string;

  constructor() {
    super();
    this.mfaEnabled = false;
    this.mfaLoading = true;
    this.setup = null;
    this.disabling = false;
    this.code = '';
    this.busy = false;
    this.connections = [];
    this.connectionsLoading = true;
    this.aiOrder = [];
    this.orderSaving = false;
    this.aiConversation = {};
    this.placeSavingId = '';
    this.confirmingId = '';
    this.revokingId = '';
    this.ending = false;
    this.endEmail = '';
  }

  createRenderRoot() {
    return this;
  }

  connectedCallback() {
    super.connectedCallback();
    void this.loadMfa();
    void this.loadConnections();
  }

  private async loadMfa() {
    try {
      const data = await api('/api/auth/mfa');
      this.mfaEnabled = data.enabled === true;
    } catch {
      this.mfaEnabled = false;
    } finally {
      this.mfaLoading = false;
    }
  }

  private async loadConnections() {
    try {
      const data = await api('/api/auth/ai-connections');
      this.connections = Array.isArray(data.connections) ? data.connections : [];
      this.aiOrder = Array.isArray(data.ai_order) ? data.ai_order.filter((id: unknown): id is string => typeof id === 'string') : [];
      this.aiConversation = readAiConversationPlaces(data.ai_conversation);
    } catch (error) {
      showToast(getErrorMessage(error), 'error');
    } finally {
      this.connectionsLoading = false;
    }
  }

  private async startSetup() {
    this.busy = true;
    try {
      const data = await api('/api/auth/mfa/enroll', { method: 'POST', json: {} });
      this.setup = { factorId: data.factor_id, qrCode: data.qr_code, secret: data.secret };
      this.code = '';
    } catch (error) {
      showToast(getErrorMessage(error), 'error');
    } finally {
      this.busy = false;
    }
  }

  private async activate(event: Event) {
    event.preventDefault();
    if (!this.setup) return;
    this.busy = true;
    try {
      await api('/api/auth/mfa/activate', { method: 'POST', json: { factor_id: this.setup.factorId, code: this.code } });
      this.mfaEnabled = true;
      this.setup = null;
      this.code = '';
      showToast(t('mfaEnabledToast') as string, 'success');
      // Turning it on ends the AI connections that were granted on the password alone.
      void this.loadConnections();
    } catch (error) {
      showToast(getErrorMessage(error), 'error');
      // The factor is on even when its AI connections could not be ended; show both as they are.
      if ((error as { code?: string }).code === 'mfa_enabled_ai_connections_left') {
        this.mfaEnabled = true;
        this.setup = null;
        this.code = '';
        void this.loadConnections();
      }
    } finally {
      this.busy = false;
    }
  }

  private async disable(event: Event) {
    event.preventDefault();
    this.busy = true;
    try {
      await api('/api/auth/mfa/disable', { method: 'POST', json: { code: this.code } });
      this.mfaEnabled = false;
      this.disabling = false;
      this.code = '';
      showToast(t('mfaDisabledToast') as string, 'success');
    } catch (error) {
      showToast(getErrorMessage(error), 'error');
    } finally {
      this.busy = false;
    }
  }

  /** Each connected AI once, in the principal's order, then the rest as listed (newest connection first). */
  private orderedClientIds(): string[] {
    const ids = [...new Set(this.connections.map((connection) => connection.client_id))];
    const rank = (id: string) => {
      const index = this.aiOrder.indexOf(id);
      return index === -1 ? this.aiOrder.length : index;
    };
    return ids.map((id, listed) => ({ id, listed })).sort((a, b) => rank(a.id) - rank(b.id) || a.listed - b.listed).map((row) => row.id);
  }

  private async move(clientId: string, step: -1 | 1) {
    const ids = this.orderedClientIds();
    const from = ids.indexOf(clientId);
    const to = from + step;
    if (from === -1 || to < 0 || to >= ids.length) return;
    [ids[from], ids[to]] = [ids[to], ids[from]];
    const previous = this.aiOrder;
    this.aiOrder = ids;
    this.orderSaving = true;
    try {
      await api('/api/profile/preferences', { method: 'PATCH', json: { preferences: { ai_order: ids } } });
      showToast(t('aiConnectionOrderSaved') as string, 'success');
    } catch (error) {
      this.aiOrder = previous;
      showToast(getErrorMessage(error), 'error');
    } finally {
      this.orderSaving = false;
    }
  }

  private async setPlace(clientId: string, place: AiConversationPlace) {
    const previous = this.aiConversation;
    const next = { ...previous, [clientId]: place };
    this.aiConversation = next;
    this.placeSavingId = clientId;
    try {
      await api('/api/profile/preferences', { method: 'PATCH', json: { preferences: { ai_conversation: next } } });
      showToast(t('aiConversationPlaceSaved') as string, 'success');
    } catch (error) {
      this.aiConversation = previous;
      showToast(getErrorMessage(error), 'error');
    } finally {
      this.placeSavingId = '';
    }
  }

  private renderPlace(clientId: string) {
    const id = `aiConversationPlace-${clientId}`;
    const place = this.aiConversation[clientId] ?? 'conversation';
    return html`
      <div style="display:flex;flex-wrap:wrap;align-items:center;gap:6px 10px;margin-top:10px;">
        <label for=${id} style="display:flex;align-items:center;gap:6px;font-size:13px;">
          <span class="mi mi-sm" style="color:var(--text-muted);" aria-hidden="true">forum</span>${t('aiConversationPlaceLabel')}
        </label>
        <select id=${id} .value=${place} ?disabled=${this.placeSavingId === clientId}
          style="min-height:34px;padding:6px 8px;border:1px solid var(--border);border-radius:var(--radius);background:var(--bg);color:var(--text);font:inherit;font-size:13px;"
          @change=${(event: Event) => this.setPlace(clientId, (event.target as HTMLSelectElement).value as AiConversationPlace)}>
          ${AI_CONVERSATION_PLACES.map((value) => html`<option value=${value} ?selected=${value === place}>${t(PLACE_LABEL_KEYS[value])}</option>`)}
        </select>
      </div>
    `;
  }

  private async revoke(connection: AiConnection) {
    this.revokingId = connection.id;
    try {
      await api(`/api/auth/ai-connections/${encodeURIComponent(connection.id)}`, { method: 'DELETE' });
      this.connections = this.connections.filter((item) => item.id !== connection.id);
      showToast(t('aiConnectionRevokedToast', { name: connection.client_name }) as string, 'success');
    } catch (error) {
      showToast(getErrorMessage(error), 'error');
    } finally {
      this.revokingId = '';
      this.confirmingId = '';
    }
  }

  // #3490: ending one's own use signs out at once, so the page goes back to the login screen.
  private async endAccount(event: Event) {
    event.preventDefault();
    this.busy = true;
    try {
      await api('/api/auth/account-end', { method: 'POST', json: { confirm_email: this.endEmail } });
      showToast(t('accountEndDoneToast') as string, 'success');
      window.setTimeout(() => window.location.reload(), 1500);
    } catch (error) {
      showToast(getErrorMessage(error), 'error');
      this.busy = false;
    }
  }

  private renderAccountEnd() {
    if (!this.ending) {
      return html`
        <div class="my-connections-actions">
          <button type="button" class="btn btn-outline" @click=${() => { this.ending = true; this.endEmail = ''; }}>${t('accountEndBtn')}</button>
        </div>
      `;
    }
    return html`
      <form style="display:flex;flex-wrap:wrap;align-items:flex-end;gap:8px;" @submit=${(event: Event) => this.endAccount(event)}>
        <div class="field" style="margin:0;min-width:240px;">
          <label for="accountEndEmail">${t('accountEndEmailLabel')}</label>
          <input type="email" id="accountEndEmail" autocomplete="off" required
            .value=${this.endEmail}
            @input=${(event: Event) => { this.endEmail = (event.target as HTMLInputElement).value; }}>
        </div>
        <button type="submit" class="btn btn-danger" ?disabled=${this.busy}>${t('accountEndConfirm')}</button>
        <button type="button" class="btn btn-outline" ?disabled=${this.busy} @click=${() => { this.ending = false; this.endEmail = ''; }}>${t('cancel')}</button>
      </form>
    `;
  }

  private formatTime(iso: string): string {
    const date = new Date(iso);
    if (Number.isNaN(date.getTime())) return '';
    return new Intl.DateTimeFormat(currentLang === 'en' ? 'en-US' : 'ja-JP', {
      timeZone: userTimezone, year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
    }).format(date);
  }

  private codeInput(id: string) {
    return html`<input
      type="text"
      id=${id}
      autocomplete="one-time-code"
      inputmode="numeric"
      maxlength="7"
      required
      placeholder="123456"
      style="max-width:140px;"
      .value=${this.code}
      @input=${(event: Event) => { this.code = (event.target as HTMLInputElement).value; }}
    >`;
  }

  private renderMfa() {
    if (this.mfaLoading) return html`<div class="settings-section-placeholder">${t('myConnectionsLoading')}</div>`;
    if (this.mfaEnabled) {
      return html`
        <div style="display:flex;align-items:center;gap:6px;font-size:13px;">
          <span class="mi mi-sm" style="color:var(--success);" aria-hidden="true">check_circle</span>
          <span>${t('mfaStatusOn')}</span>
        </div>
        ${this.disabling ? html`
          <form style="display:flex;flex-wrap:wrap;align-items:flex-end;gap:8px;margin-top:10px;" @submit=${(event: Event) => this.disable(event)}>
            <div class="field" style="margin:0;">
              <label for="mfaDisableCode">${t('mfaDisableCodeLabel')}</label>
              ${this.codeInput('mfaDisableCode')}
            </div>
            <button type="submit" class="btn btn-outline" ?disabled=${this.busy}>${t('mfaDisableConfirm')}</button>
            <button type="button" class="btn btn-outline" ?disabled=${this.busy} @click=${() => { this.disabling = false; this.code = ''; }}>${t('cancel')}</button>
          </form>
        ` : html`
          <div class="my-connections-actions" style="margin-top:10px;">
            <button type="button" class="btn btn-outline" @click=${() => { this.disabling = true; this.code = ''; }}>${t('mfaDisableBtn')}</button>
          </div>
        `}
      `;
    }
    if (this.setup) {
      return html`
        <ol style="margin:0 0 10px 18px;padding:0;font-size:13px;line-height:1.7;">
          <li>${t('mfaSetupStep1')}</li>
          <li>${t('mfaSetupStep2')}</li>
        </ol>
        <!-- Scanners need dark modules on white in either theme, so the QR keeps a white ground. -->
        <img src=${this.setup.qrCode} alt=${t('mfaQrAlt')} width="168" height="168" style="display:block;background:#fff;border:1px solid var(--border);border-radius:var(--radius);padding:6px;">
        <div style="font-size:12px;color:var(--text-muted);margin-top:6px;">
          ${t('mfaSecretLabel')} <code style="user-select:all;word-break:break-all;">${this.setup.secret}</code>
        </div>
        <form style="display:flex;flex-wrap:wrap;align-items:flex-end;gap:8px;margin-top:10px;" @submit=${(event: Event) => this.activate(event)}>
          <div class="field" style="margin:0;">
            <label for="mfaSetupCode">${t('mfaCodeLabel')}</label>
            ${this.codeInput('mfaSetupCode')}
          </div>
          <button type="submit" class="btn btn-primary" ?disabled=${this.busy}>${t('mfaActivateBtn')}</button>
          <button type="button" class="btn btn-outline" ?disabled=${this.busy} @click=${() => { this.setup = null; this.code = ''; }}>${t('cancel')}</button>
        </form>
        <div class="my-connections-hint" style="margin-top:8px;">${t('mfaActivateHint')}</div>
      `;
    }
    return html`
      <div style="display:flex;align-items:center;gap:6px;font-size:13px;">
        <span class="mi mi-sm" style="color:var(--warning-text);" aria-hidden="true">radio_button_unchecked</span>
        <span>${t('mfaStatusOff')}</span>
      </div>
      <div class="my-connections-actions" style="margin-top:10px;">
        <button type="button" class="btn btn-primary" ?disabled=${this.busy} @click=${() => this.startSetup()}>
          <span class="mi mi-sm" aria-hidden="true">phonelink_lock</span>${t('mfaEnableBtn')}
        </button>
      </div>
    `;
  }

  private renderConnectionRow(connection: AiConnection) {
    const confirming = this.confirmingId === connection.id;
    return html`
      <li style="display:flex;flex-wrap:wrap;align-items:center;gap:6px 12px;padding:6px 0;border-top:1px solid var(--border);font-size:12px;">
        <span style="flex:1;min-width:180px;color:var(--text-muted);">
          ${t('aiConnectionConnectedAt', { time: this.formatTime(connection.connected_at) })}
          ・${t('aiConnectionLastUsedAt', { time: this.formatTime(connection.last_used_at) })}
        </span>
        ${confirming ? html`
          <button type="button" class="btn btn-danger btn-sm" ?disabled=${this.revokingId === connection.id} @click=${() => this.revoke(connection)}>${t('aiConnectionRevokeConfirm')}</button>
          <button type="button" class="btn btn-outline btn-sm" ?disabled=${this.revokingId === connection.id} @click=${() => { this.confirmingId = ''; }}>${t('cancel')}</button>
        ` : html`
          <button type="button" class="btn btn-outline btn-sm" @click=${() => { this.confirmingId = connection.id; }}>${t('aiConnectionRevoke')}</button>
        `}
      </li>
    `;
  }

  /** #3776: one card per AI; its connections (one per sign-in) fold under it. */
  private renderAi(clientId: string, ids: string[]) {
    const connections = this.connections.filter((connection) => connection.client_id === clientId);
    const position = ids.indexOf(clientId);
    const lastUsed = connections.map((connection) => connection.last_used_at).sort().at(-1) ?? '';
    return html`
      <li style="padding:12px 14px;border:1px solid var(--border);border-radius:var(--radius);margin-top:8px;">
        <div style="display:flex;align-items:center;gap:8px;">
          <span class="mi" style="color:var(--claude-accent);" aria-hidden="true">smart_toy</span>
          <div style="flex:1;min-width:0;">
            <div style="font-size:14px;font-weight:600;">
              ${ids.length > 1 ? html`<span style="font-weight:400;color:var(--text-muted);margin-right:6px;">${t('aiConnectionOrder', { n: position + 1 })}</span>` : nothing}${connections[0]?.client_name ?? clientId}
            </div>
            <div style="font-size:12px;color:var(--text-muted);">${t('aiConnectionLastUsedAt', { time: this.formatTime(lastUsed) })}</div>
          </div>
          ${ids.length > 1 ? html`
            <button type="button" class="btn btn-outline btn-sm" title=${t('aiConnectionMoveUp')} aria-label=${t('aiConnectionMoveUp')}
              ?disabled=${this.orderSaving || position === 0} @click=${() => this.move(clientId, -1)}>
              <span class="mi mi-sm" aria-hidden="true">arrow_upward</span>
            </button>
            <button type="button" class="btn btn-outline btn-sm" title=${t('aiConnectionMoveDown')} aria-label=${t('aiConnectionMoveDown')}
              ?disabled=${this.orderSaving || position === ids.length - 1} @click=${() => this.move(clientId, 1)}>
              <span class="mi mi-sm" aria-hidden="true">arrow_downward</span>
            </button>
          ` : nothing}
        </div>
        ${this.renderPlace(clientId)}
        <details style="margin-top:10px;">
          <summary style="cursor:pointer;font-size:12px;color:var(--text-muted);">${t('aiConnectionSessions', { n: connections.length })}</summary>
          <ul style="list-style:none;margin:6px 0 0;padding:0;">${connections.map((connection) => this.renderConnectionRow(connection))}</ul>
        </details>
      </li>
    `;
  }

  private renderConnections() {
    if (this.connectionsLoading) return html`<div class="settings-section-placeholder">${t('myConnectionsLoading')}</div>`;
    if (this.connections.length === 0) return html`<div class="settings-section-placeholder">${t('aiConnectionsEmpty')}</div>`;
    const ids = this.orderedClientIds();
    return html`<ul style="list-style:none;margin:0;padding:0;">${ids.map((id) => this.renderAi(id, ids))}</ul>`;
  }

  render() {
    return html`
      <div style="margin-top:20px;padding-top:16px;border-top:1px solid var(--border);">
        <h4 style="display:flex;align-items:center;gap:6px;margin:0 0 6px;font-size:14px;">
          <span class="mi mi-sm" aria-hidden="true">phonelink_lock</span>${t('mfaTitle')}
        </h4>
        <div class="group-note" style="margin-bottom:10px;">
          <span class="mi" aria-hidden="true">info</span>
          <span>${t('mfaNote')}</span>
        </div>
        ${this.renderMfa()}
      </div>
      <div style="margin-top:20px;padding-top:16px;border-top:1px solid var(--border);">
        <h4 style="display:flex;align-items:center;gap:6px;margin:0 0 6px;font-size:14px;">
          <span class="mi mi-sm" aria-hidden="true">smart_toy</span>${t('aiConnectionsTitle')}
        </h4>
        <div class="group-note" style="margin-bottom:6px;">
          <span class="mi" aria-hidden="true">info</span>
          <span>${t('aiConnectionsNote')}</span>
        </div>
        ${this.orderedClientIds().length > 1 ? html`
          <div class="group-note" style="margin-bottom:6px;">
            <span class="mi" aria-hidden="true">swap_vert</span>
            <span>${t('aiConnectionsOrderNote')}</span>
          </div>
        ` : nothing}
        ${this.connections.length > 0 ? html`
          <div class="group-note" style="margin-bottom:6px;">
            <span class="mi" aria-hidden="true">forum</span>
            <span>${t('aiConversationPlaceHint')}</span>
          </div>
        ` : nothing}
        ${this.renderConnections()}
      </div>
      <div style="margin-top:20px;padding-top:16px;border-top:1px solid var(--border);">
        <h4 style="display:flex;align-items:center;gap:6px;margin:0 0 6px;font-size:14px;">
          <span class="mi mi-sm" aria-hidden="true">logout</span>${t('accountEndTitle')}
        </h4>
        <div class="group-note" style="margin-bottom:10px;">
          <span class="mi" aria-hidden="true">info</span>
          <span>${t('accountEndNote')}</span>
        </div>
        ${this.renderAccountEnd()}
      </div>
    `;
  }
}

defineZenComponent('zen-account-security-section', ZenAccountSecuritySection);
